import React, { useState, useEffect, useRef, useContext } from 'react';
import { Box, Button, IconButton, Typography, TextField, MenuItem, Select, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AppStorage } from '../Utils/Context/AppContext';
import { showToast } from '../Utils';
import Request from '../Utils/Request/Request';

const allOptions = {
  "Info Table": [
    { text: "Customer Address", value: "customerAddress" },
    { text: "Customer Name", value: "customerName" },
    { text: "Invoice Date", value: "invoiceDate" },
    { text: "Invoice No.", value: "invoiceNo" },
    { text: "Document Number", value: "documentNumber" },
    { text: "Original Invoice No.", value: "originalInvoiceNo" },
    { text: "Original Invoice Date", value: "originalInvoiceDate" },
    { text: "Notes", value: "notes" },
    { text: "Notes 2", value: "notes2" },
    { text: "PO No.", value: "poNo" },
    { text: "Payment Terms", value: "paymentTerms" },
    { text: "Vat NO.", value: "vatNo" }
  ],
  "Product Table": [
    { text: "Description", value: "description" },
    { text: "Item Discount", value: "itemDiscount" },
    { text: "Item Total After Discount", value: "itemTotalAfterDiscount" },
    { text: "Item Vat", value: "itemVat" },
    { text: "Product Name", value: "productName" },
    { text: "Qty", value: "qty" },
    { text: "Sr. No", value: "srNo" },
    { text: "Item Total", value: "itemTotal" },
    { text: "Item Total After Vat", value: "itemTotalAfterVat" },
    { text: "Unit", value: "unit" },
    { text: "Unit Price", value: "unitPrice" }
  ],
  "Total Table": [
    { text: "Discount", value: "discount" },
    { text: "Extra Values", value: "extraValues" },
    { text: "Added Values", value: "addedValues" },
    { text: "Net Amount", value: "netAmount" },
    { text: "Total Before Vat", value: "totalBeforeVat" },
    { text: "Total After Discount", value: "totalAfterDiscount" },
    { text: "Vat%", value: "vat" }
  ]
};

const TemplateTable = ({ data, onChange }) => {
  const [selectedTableType, setSelectedTableType] = useState(data?.tableType || '');
  const AppContext = useContext(AppStorage);
  const [fields, setFields] = useState(data?.value || []);
  const [allExtraLabels, setAllExtraLabels] = useState([])

    const getAllExtraAdminLabel = () => {
      Request.get("/Labels")
        .then((result) => {
          console.log(result?.data || []);
          setAllExtraLabels(result?.data || []);
        })
        .catch((err) => {
          console.error("Error fetching labels:", err);
          showToast("error", "Something went wrong while fetching labels.");
        });
    };

  useEffect(() => {
      if (AppContext?.userAuthToken) {
        getAllExtraAdminLabel();
      }
    }, [AppContext?.userAuthToken]);

  useEffect(() => {
    if (selectedTableType !== data.tableType) {
      setFields([]);
      setSelectedTableType(data.tableType);
    }
  }, [data.tableType]);

  const handleTableTypeChange = (event) => {
    const newTableType = event.target.value;
    setSelectedTableType(newTableType);
    setFields([]);
  
    onChange({
      type: "table", 
      tableType: newTableType, 
      value: [] 
    });
  };
  
  const handleFieldChange = (index, field, value) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], [field]: value };
    setFields(newFields);
  
    onChange({
      type: "table",
      tableType: selectedTableType,
      value: newFields, 
    });
  };
  
  const addField = () => {
    setFields([...fields, { label: '', value: '' }]);
  };

  const removeField = (index) => {
    const newFields = fields.filter((_, i) => i !== index);
    setFields(newFields);
    onChange({ tableType: selectedTableType, value: newFields });
  };

  const options = allOptions[selectedTableType] || [];

  const allOptionsWithExtraLabels = selectedTableType === "Product Table" 
  ? [
      ...options,
      ...allExtraLabels.map(label => ({
        text: `${label.valueLabel} (by: ${label.user.fullName})`,
        value: label.id
      }))
    ]
  : options;

  const [boxWidth, setBoxWidth] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    const updateWidth = () => {
      if (boxRef.current) {
        setBoxWidth(boxRef.current.offsetWidth);
      }
    };

    const resizeObserver = new ResizeObserver(updateWidth);
    if (boxRef.current) {
      resizeObserver.observe(boxRef.current);
    }

    // Clean up observer on unmount
    return () => {
      if (boxRef.current) {
        resizeObserver.unobserve(boxRef.current);
      }
    };
  }, []);
  return (
    <Box ref={boxRef} sx={{ width: '100%' }}>
      <Typography variant="body1" color={"#365b6d"}>Table Type:</Typography>
      <Select
        value={selectedTableType}
        onChange={handleTableTypeChange}
        displayEmpty
      >
        <MenuItem value="" disabled>Select Table Type</MenuItem>
        {Object.keys(allOptions).map((type) => (
          <MenuItem key={type} value={type}>
            {type}
          </MenuItem>
        ))}
      </Select>

      {selectedTableType && (
        <>
          
          {fields.map((field, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2, flexDirection: boxWidth < 400 ? 'column' : 'row'  }}>
             <TextField
              label="Custom Text"
              value={field.label}  
              onChange={(e) => handleFieldChange(index, 'label', e.target.value)}
              fullWidth
              InputProps={{
                readOnly: allExtraLabels.some(label => label.id === field.value), 
              }}
              // helperText={allExtraLabels.some(label => label.id === field.value) ? "Read-only, value matches extra label" : ""}
              variant="outlined"
            />

              <Select
                value={field.value}
                onChange={(e) => handleFieldChange(index, 'value', e.target.value)}
                displayEmpty
                fullWidth
              >
                <MenuItem value="" disabled>Select Option</MenuItem>
                {allOptionsWithExtraLabels.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.text}
                  </MenuItem>
                ))}
              </Select>
              <Tooltip title="Remove field">
                <IconButton
                  color="error"
                  aria-label="Remove field"
                  onClick={() => removeField(index)}
                  sx={{ flexShrink: 0, alignSelf: boxWidth < 400 ? "flex-end" : "center" }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
          <Button variant="outlined" onClick={addField} sx={{ mt: 2, display:"block" }}>
            Add Field
          </Button>
        </>
      )}
    </Box>
  );
};

export default TemplateTable;
